import * as logger from 'loglevel';
import dayjs from 'dayjs';
import differenceInDays from 'date-fns/differenceInDays';
import transformHistogramSmart from './transformRequest/transformHistogramSmart';
import axiosWrapper from '../../helpers/axiosWrapper';
import { NO_DURATION_DEFINED } from '../../helpers/axiosWrapper/cache';

/**
 * Here we find the lowest fare
 */
const lowestFare = (data, blackoutDates = []) => {
  const lowestFaresByMonth = new Map();
  const validFares = data.filter((item) => {
    if (blackoutDates.length !== 0) {
      return (
        item.fares.length > 0 &&
        item.fares[0].priceSpecification.totalPrice &&
        !isBlackoutDate(dayjs(item.date).toDate(), blackoutDates)
      );
    }
    return item.fares.length > 0 && item.fares[0].priceSpecification.totalPrice;
  });
  validFares.forEach((item) => {
    const month = dayjs(item.date).month();
    const price = item.fares[0].priceSpecification.totalPrice;

    if (!lowestFaresByMonth.has(month) || price < lowestFaresByMonth.get(month)) {
      lowestFaresByMonth.set(month, price);
    }
  });

  return lowestFaresByMonth;
};

const extractPriceWithoutCurrencyCode = (price) => {
  const match = price.match(/(\d+(?:\.\d+)?[Kk]?)/);
  return match[0];
};
/**
 * The histograms API does not returns the journeyType
 * so this map allows us to use query sent parameters to do it
 */
const journeyTypeMap = {
  ROUND_TRIP: 'rt',
  ONE_WAY: 'ow',
  MULTI_CITY: 'mc',
  EACH_WAY: 'ew'
};

const isBlackoutDate = (date, blackoutDates) => {
  const newDate = dayjs(date);
  for (let index = 0; index < blackoutDates.length; index++) {
    const element = blackoutDates[index];
    if (
      dayjs(element.start).isSame(newDate) ||
      dayjs(element.end).isSame(newDate) ||
      (dayjs(newDate).isAfter(element.start) && dayjs(newDate).isBefore(element.end))
    ) {
      return true;
    }
  }
  return false;
};

/**
 * The group routes API returns the data in non linear format
 * so this function will intend normalize the data for the components
 * @param {*} faresRawData
 * @param {*} query
 */
const transformData = (histogramRawData, filter) => {
  const { blackoutDates } = filter;
  const { ignoreCurrencyCode = false } = filter;
  const lowestFaresByMonth = lowestFare(histogramRawData.histogram, blackoutDates);
  const histogram = histogramRawData.histogram.map((item) => {
    let fareItem = item.fares && Array.isArray(item.fares) ? item.fares[0] : null;
    let newFare;
    if (fareItem) {
      let { searchDate } = fareItem;
      // For date in safary y ie
      if (searchDate && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+[+|-]\d{4}/.test(searchDate)) {
        searchDate = `${searchDate.substring(0, searchDate.length - 2)}:${searchDate.substring(
          searchDate.length - 2
        )}`;
      }
      const {
        airline: { iataCode: airlineCode },
        priceSpecification: {
          currencyCode,
          currencySymbol,
          formattedTotalPrice,
          totalPrice,
          usdTotalPrice,
          shortenedFormattedTotalPrice,
          redemption
        },
        datacoreId,
        departureDate,
        formattedDepartureDate,
        returnDate,
        formattedReturnDate,
        isBlackoutDay,
        journeyType,
        market,
        outboundFlight: { fareClass: farenetTravelClass, fareClassInput: travelClass },
        flightDeltaDays
      } = fareItem;

      newFare = {
        airlineCode: airlineCode.toLowerCase(),
        currencyCode,
        currencySymbol,
        formattedTotalPriceForMobile: formattedTotalPrice,
        formattedTotalPrice: ignoreCurrencyCode
          ? extractPriceWithoutCurrencyCode(formattedTotalPrice)
          : formattedTotalPrice,
        formattedUsdTotalPrice: formattedTotalPrice,
        shortenedFormattedTotalPrice: ignoreCurrencyCode
          ? extractPriceWithoutCurrencyCode(shortenedFormattedTotalPrice)
          : shortenedFormattedTotalPrice,
        shortenedFormattedTotalPriceForMobile: shortenedFormattedTotalPrice,
        totalPrice,
        usdTotalPrice,
        datacoreId,
        dataType: 'DATA_API',
        departureDate,
        formattedDepartureDate,
        returnDate,
        formattedReturnDate,
        destination: filter.destination,
        origin: filter.origin,
        farenetTravelClass,
        travelClass,
        flightDeltaDays,
        journeyType: journeyTypeMap[journeyType],
        searchDate,
        isBlackoutDay,
        flightType: journeyType,
        market
      };
      if (filter?.redemptionUnit === 'MILES' || filter?.redemptionUnit === 'POINTS') {
        newFare['redemption'] = {
          ...redemption
        };
      }
    } else {
      newFare = { dataType: 'EMPTY_FROM_API', departureDate: item.date };
    }
    if (blackoutDates) {
      newFare.isBlackoutDay = isBlackoutDate(item.date, blackoutDates);
    }

    return {
      day: dayjs(item.date).toDate(),
      fareItem: newFare,
      isLowest:
        newFare.dataType === 'DATA_API'
          ? newFare.totalPrice === lowestFaresByMonth.get(dayjs(item.date).month())
          : false
    };
  });

  //INFO: This fix was necessary since the EM API does not return the range of days specified when requesting the returnDateRange
  const { returnDateRange: { start, end } = {} } = filter;
  let differentDays = differenceInDays(new Date(end), new Date(start));
  if (start && end && differentDays !== histogram.length) {
    let dayCounter = 2;
    while (differentDays > 0) {
      const date = dayjs(new Date(start)).add(dayCounter, 'day');
      let fareItemFill = {
        day: date.toDate(),
        fareItem: { dataType: 'EMPTY_FROM_API', departureDate: date.format('YYYY-MM-DD') },
        isLowest: false
      };
      if (filter.blackoutDates) {
        fareItemFill.isBlackoutDay = isBlackoutDate(date.toDate(), filter.blackoutDates);
      }
      const found = histogram.find(
        (ele) => dayjs(ele.day).format('YYYY-MM-DD') === date.format('YYYY-MM-DD')
      );
      if (!found) {
        histogram.push(fareItemFill);
      }
      dayCounter = dayCounter + 1;
      differentDays = differentDays - 1;
    }
  }

  return histogram;
};

/**
 * Makes sure the histogram response is complain with a basic schema
 * @param {*} histogram
 */
const validateHistogramResponse = (histograms) => {
  const validation = { isValid: true, message: '' };
  if (!histograms || !histograms.histogram || !Array.isArray(histograms.histogram)) {
    validation.isValid = false;
    return {
      isValid: false,
      data: histograms,
      message: 'INTEGRATION__HISTOGRAM: Histograms must be an Array'
    };
  }
  if (histograms.length > 0) {
    const histogram = histograms[0];
    if (!histogram.origin || !histogram.destination) {
      return {
        isValid: false,
        data: histogram,
        message:
          'INTEGRATION__HISTOGRAM: Histogram SCHEMA is invalid, please check Histograms API documentation.'
      };
    }
  }
  return validation;
};

const formatDateRange = (start, end) => ({
  start: dayjs(start).format('YYYY-MM-DD'),
  end: dayjs(end).format('YYYY-MM-DD')
});

const processFilters = (filterCopy, filter) => {
  delete filterCopy.combinable;
  delete filterCopy.flightType;
  filterCopy.journeyType = 'ONE_WAY';
  filterCopy.departure = formatDateRange(filter.dateRange.start, filter.dateRange.end);
};

const createFilterConfig = (filterCopy, filter, isReturn = false) => {
  let filterConfig = { ...filterCopy };
  if (isReturn) {
    filterConfig.origin = filter.destination;
    filterConfig.destination = filter.origin;
  }
  processFilters(filterConfig, filter);
  filterConfig.departure = formatDateRange(
    isReturn ? filterCopy.dateRange?.returnDate : filterCopy.dateRange?.departureDate,
    isReturn ? filterCopy.dateRange?.returnDate : filterCopy.dateRange?.departureDate
  );
  return filterConfig;
};

function findFare(data, departureDate, returnDate) {
  const fare = data.filter(
    (item) =>
      dayjs(departureDate).format('MM/DD/YYYY') === dayjs(item.day).format('MM/DD/YYYY') &&
      item?.fareItem?.returnDate &&
      dayjs(returnDate).format('YYYY-MM-DD') ===
        dayjs(item?.fareItem?.returnDate).format('YYYY-MM-DD')
  )[0];
  return fare;
}
function simpleFindFare(data, date) {
  const fare = data.filter(
    (item) => dayjs(date).format('MM/DD/YYYY') === dayjs(item.day).format('MM/DD/YYYY')
  )[0];
  return fare;
}

/**
 * Retrieve fares from the GroupRoutes Endpoint
 * @param {*} filter
 * @param {*} integrationSettings
 */
const getHistogramsSmart = (filter, integrationSettings, _, meta, pageContext) => {
  return new Promise((resolve, reject) => {
    if (!integrationSettings) {
      const MESSAGE = `INTEGRATION__HISTOGRAM: **integrationSettings** not especified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    const { v2FaresHistogramSmart: histogramApiSettings } = integrationSettings;
    const { cache = false, cacheDuration = NO_DURATION_DEFINED } = histogramApiSettings;
    const filterCopy = { ...filter };

    if (
      !histogramApiSettings ||
      !histogramApiSettings.method ||
      !histogramApiSettings.endPoint ||
      !histogramApiSettings.headers
    ) {
      const MESSAGE = `INTEGRATION__HISTOGRAM: **integrationSettings.histogramApiSettings** configuration issues.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    function createRequest(baseFilterConfig, dateRangeKey) {
      const filterConfig = { ...baseFilterConfig };

      filterConfig.departure = {
        start: dayjs(filterConfig[dateRangeKey].dateRange.start).format('YYYY-MM-DD'),
        end: dayjs(filterConfig[dateRangeKey].dateRange.end).format('YYYY-MM-DD')
      };

      if (dateRangeKey === 'dateBeforeReurnDate') {
        filterConfig.return = {
          start: dayjs(filterConfig[dateRangeKey].returnDateRange.start).format('YYYY-MM-DD'),
          end: dayjs(filterConfig[dateRangeKey].returnDateRange.end).format('YYYY-MM-DD')
        };
      }

      filterConfig.journeyType = filterConfig.flightType;
      delete filterConfig.flightType;

      return [
        axiosWrapper(
          {
            method: histogramApiSettings.method,
            url: histogramApiSettings.endPoint,
            headers: { ...histogramApiSettings.headers },
            data: transformHistogramSmart(filterConfig)
          },
          {
            resource: 'INTEGRATION__HISTOGRAM',
            meta,
            integrationSettings,
            pageContext,
            cache,
            cacheDuration
          }
        ),
        filterConfig
      ];
    }

    const fetchHistogram = (filter) => {
      return axiosWrapper(
        {
          method: histogramApiSettings.method,
          url: histogramApiSettings.endPoint,
          headers: { ...histogramApiSettings.headers },
          data: transformHistogramSmart(filter)
        },
        {
          resource: 'INTEGRATION__HISTOGRAM',
          meta,
          integrationSettings,
          pageContext,
          cache,
          cacheDuration
        }
      );
    };

    const fetchHistograms = (filters) => {
      return Promise.all(
        filters.map((filter) =>
          axiosWrapper(
            {
              method: histogramApiSettings.method,
              url: histogramApiSettings.endPoint,
              headers: { ...histogramApiSettings.headers },
              data: transformHistogramSmart(filter)
            },
            {
              resource: 'INTEGRATION__HISTOGRAM',
              meta,
              integrationSettings,
              pageContext,
              cache,
              cacheDuration
            }
          )
        )
      );
    };

    const handleHistogramResponses = (responses, filters) => {
      let histograms = [];
      responses.forEach(({ data }, index) => {
        const validation = validateHistogramResponse(data);
        if (validation.isValid) {
          histograms.push(transformData(data, filters[index]));
        } else {
          logger.error(validation.message, validation.data);
          reject(responses.data);
        }
      });
      resolve(histograms.flat());
    };

    const handleHistogramResults = (histograms, filterCopy) => {
      return new Promise((resolve, reject) => {
        let results = histograms.flat();
        let fare = findFare(
          results,
          filterCopy.dateRange?.departureDate,
          filterCopy.dateRange?.returnDate
        );

        if (!fare) {
          let configuration = { ...filterCopy };
          configuration.departure = {
            start: dayjs(filterCopy.dateRange.departureDate).format('YYYY-MM-DD'),
            end: dayjs(filterCopy.dateRange.departureDate).format('YYYY-MM-DD')
          };
          configuration.return = {
            start: dayjs(filterCopy.dateRange.returnDate).format('YYYY-MM-DD'),
            end: dayjs(filterCopy.dateRange.returnDate).format('YYYY-MM-DD')
          };
          configuration.histogramType = 'INBOUND';
          configuration.journeyType = configuration.flightType;
          delete configuration.flightType;

          fetchHistogram(configuration).then((response) => {
            const validation = validateHistogramResponse(response.data);
            if (validation.isValid) {
              logger.info('INTEGRATION__HISTOGRAM: Successful request', response.data);
              let data = transformData(response.data, configuration);
              results.push(data[0]);
              resolve(results);
            } else {
              logger.error(validation.message, validation.data);
              reject(response.data);
            }
          });
        } else {
          resolve(histograms.flat());
        }
      });
    };

    const handleHistogramResponseInterval = (histograms, filterCopy) => {
      return new Promise((resolve, reject) => {
        let results = histograms.flat();
        let configuration = { ...filterCopy };
        configuration.departure = {
          start: dayjs(filterCopy.dateRange.departureDate).format('YYYY-MM-DD'),
          end: dayjs(filterCopy.dateRange.departureDate).format('YYYY-MM-DD')
        };
        configuration.return = {
          start: dayjs(filterCopy.dateRange.returnDate).format('YYYY-MM-DD'),
          end: dayjs(filterCopy.dateRange.returnDate).format('YYYY-MM-DD')
        };
        configuration.histogramType = 'INBOUND';
        delete configuration.flightType;
        fetchHistogram(configuration).then((response) => {
          const validation = validateHistogramResponse(response.data);
          if (validation.isValid) {
            logger.info('INTEGRATION__HISTOGRAM: Successful request', response.data);
            let data = transformData(response.data, configuration);
            results.push(data[0]);
            resolve(results);
          } else {
            logger.error(validation.message, validation.data);
            reject(response.data);
          }
        });
      });
    };

    const handlerHistogramResponse = (histograms, filterCopy) => {
      return new Promise((resolve, reject) => {
        let results = histograms.flat();
        let configuration = { ...filterCopy };
        configuration.departure = {
          start: dayjs(filterCopy.dateRange.departureDate).format('YYYY-MM-DD'),
          end: dayjs(filterCopy.dateRange.departureDate).format('YYYY-MM-DD')
        };
        configuration.histogramType = 'OUTBOUND';
        delete configuration.flightType;
        fetchHistogram(configuration).then((response) => {
          const validation = validateHistogramResponse(response.data);
          if (validation.isValid) {
            logger.info('INTEGRATION__HISTOGRAM: Successful request', response.data);
            let data = transformData(response.data, configuration);
            results.push(data[0]);
            resolve(results);
          } else {
            logger.error(validation.message, validation.data);
            reject(response.data);
          }
        });
      });
    };

    const handleHistogramResponse = (histograms, filterCopy) => {
      return new Promise((resolve, reject) => {
        let results = histograms.flat();
        let fare = simpleFindFare(results, filterCopy.dateRange?.departureDate);
        if (!fare) {
          let configuration = { ...filterCopy };
          configuration.departure = {
            start: dayjs(filterCopy.dateRange.departureDate).format('YYYY-MM-DD'),
            end: dayjs(filterCopy.dateRange.departureDate).format('YYYY-MM-DD')
          };
          configuration.histogramType = 'OUTBOUND';
          delete configuration.flightType;
          fetchHistogram(configuration).then((response) => {
            const validation = validateHistogramResponse(response.data);
            if (validation.isValid) {
              logger.info('INTEGRATION__HISTOGRAM: Successful request', response.data);
              let data = transformData(response.data, configuration);
              results.push(data[0]);
              resolve(results);
            } else {
              logger.error(validation.message, validation.data);
              reject(response.data);
            }
          });
        } else {
          resolve(results);
        }
      });
    };

    if (filterCopy?.dateBeforeReurnDate) {
      delete filterCopy.originCity;
      delete filterCopy.destinationCity;
      const [queryBefore, filterConfigBefore] = createRequest(filterCopy, 'dateBeforeReurnDate');
      let [queryAfter, filterConfigAfter] = [null, null];
      if (filterCopy?.dateAfterReurnDate) {
        [queryAfter, filterConfigAfter] = createRequest(filterCopy, 'dateAfterReurnDate');
      }
      Promise.all(queryAfter !== null ? [queryBefore, queryAfter] : [queryBefore]).then(
        (response) => {
          let histograms = [];
          response.forEach(({ data }, index) => {
            const validation = validateHistogramResponse(data);
            if (validation.isValid) {
              histograms.push(
                transformData(data, index === 0 ? filterConfigBefore : filterConfigAfter)
              );
            } else {
              logger.error(validation.message, validation.data);
              reject(response.data);
            }
          });
          if (
            !filterCopy.dateRange?.action?.includes('combinable') &&
            (filterCopy.dateRange?.action?.includes('filter-change') ||
              filterCopy.dateRange?.action?.includes('update-fare')) &&
            filterCopy.dateRange?.departureDate &&
            filterCopy.dateRange?.returnDate
          ) {
            handleHistogramResults(histograms, filterCopy).then(resolve).catch(reject);
          } else {
            resolve(histograms.flat());
          }
        }
      );
    } else {
      if (
        filterCopy.dateRange?.action?.includes('combinable') &&
        filterCopy.dateRange?.action?.includes('filter-change') &&
        filterCopy.dateRange?.departureDate &&
        filterCopy.dateRange?.action?.includes('--') &&
        !filterCopy.dateRange?.returnDate !== undefined
      ) {
        const returnDate = filterCopy.dateRange?.action.split('_filter-change')[0].split('--')[1];
        let filters = [];
        processFilters(filterCopy, filter);
        let filterConfig = createFilterConfig(filterCopy, filter, true);
        let filterConfig_ = createFilterConfig(filter, filter, false);
        filters.push(filterCopy, filterConfig_, {
          ...filterConfig,
          departure: formatDateRange(returnDate, returnDate)
        });
        fetchHistograms(filters).then((responses) => handleHistogramResponses(responses, filters));
      } else if (
        filterCopy.dateRange?.action?.includes('combinable') &&
        filterCopy.dateRange?.action?.includes('filter-change') &&
        filterCopy.dateRange?.departureDate &&
        !filterCopy.dateRange?.action?.includes('--') &&
        filterCopy.dateRange?.returnDate !== undefined
      ) {
        let filters = [];
        processFilters(filterCopy, filter);
        let filterConfig = createFilterConfig(filterCopy, filter);
        let filterConfig_ = createFilterConfig(filter, filter, false);
        filterCopy.origin = filter.destination;
        filterCopy.destination = filter.origin;
        filters.push(filterCopy, filterConfig_, {
          ...filterConfig,
          origin: filter.destination,
          destination: filter.origin,
          departure: formatDateRange(
            filterCopy.dateRange.returnDate,
            filterCopy.dateRange.returnDate
          )
        });
        fetchHistograms(filters).then((responses) => handleHistogramResponses(responses, filters));
      } else if (
        filterCopy.dateRange?.action?.includes('combinable') &&
        filterCopy.dateRange?.action?.includes('filter-change') &&
        filterCopy.dateRange?.departureDate &&
        filterCopy.dateRange?.returnDate
      ) {
        let filters = [];
        if (filterCopy.dateRange?.action.includes('combinable-origin-destination')) {
          processFilters(filterCopy, filter);
          let filterConfig = createFilterConfig(filterCopy, filter, true);
          if (
            dayjs(filter.dateRange.start).month() !== dayjs(filter.dateRange.departureDate).month()
          ) {
            let filterConfig_ = createFilterConfig(filter, filter, false);
            filters.push(filterCopy, filterConfig_, filterConfig);
          } else {
            filters.push(filterCopy, filterConfig);
          }
        } else if (filterCopy.dateRange?.action.includes('combinable-destination-origin')) {
          processFilters(filterCopy, filter);
          let filterConfig = createFilterConfig(filterCopy, filter);
          if (
            dayjs(filter.dateRange.start).month() !== dayjs(filter.dateRange.returnDate).month()
          ) {
            let filterConfig_ = createFilterConfig(filter, filter, true);
            filterConfig_.origin = filter.destination;
            filterConfig_.destination = filter.origin;
            filterCopy.origin = filter.destination;
            filterCopy.destination = filter.origin;
            filters.push(filterCopy, filterConfig, filterConfig_);
          } else {
            filterCopy.origin = filter.destination;
            filterCopy.destination = filter.origin;
            filters.push(filterCopy, filterConfig);
          }
        }
        fetchHistograms(filters).then((responses) => handleHistogramResponses(responses, filters));
      } else {
        //combinable
        if (filterCopy.dateRange?.action?.includes('combinable-destination-origin')) {
          filterCopy.origin = filter.destination;
          filterCopy.destination = filter.origin;
          delete filterCopy.combinable;
          filterCopy.journeyType = 'ONE_WAY';
        }
        // Format date
        filterCopy.departure = {
          start: dayjs(filter.dateRange.start).format('YYYY-MM-DD'),
          end: dayjs(filter.dateRange.end).format('YYYY-MM-DD')
        };
        if (filterCopy.returnDateRange !== undefined && filterCopy.returnDateRange !== null) {
          filterCopy.return = {
            start: dayjs(filter.returnDateRange.start).format('YYYY-MM-DD'),
            end: dayjs(filter.returnDateRange.end).format('YYYY-MM-DD')
          };
        } else if (
          !filterCopy.dateRange?.action?.includes('combinable') &&
          filterCopy.dateRange?.action?.includes('filter-change') &&
          filterCopy.dateRange?.departureDate !== undefined &&
          filterCopy.dateRange?.returnDate === undefined &&
          filter.flightType === 'ROUND_TRIP'
        ) {
          filterCopy.departure = {
            start: dayjs(filter.dateRange.departureDate).format('YYYY-MM-DD'),
            end: dayjs(filter.dateRange.departureDate).format('YYYY-MM-DD')
          };
          filterCopy.return = {
            start: dayjs(filter.dateRange.departureDate).format('YYYY-MM-DD'),
            end: dayjs(filter.dateRange.end).format('YYYY-MM-DD')
          };
        }
        filterCopy.journeyType = filterCopy.flightType;
        delete filterCopy.flightType;
        delete filterCopy.originCity;
        delete filterCopy.destinationCity;

        if (filterCopy.dateRange?.action?.includes('combinable-destination-origin-and-departure')) {
          let filters = [];
          filters.push(filterCopy);
          const filterConfig = {
            ...filterCopy,
            departure: {
              start: dayjs(filterCopy.dateRange.start).format('YYYY-MM-DD'),
              end: dayjs(filterCopy.dateRange.start).format('YYYY-MM-DD')
            },
            origin: filterCopy.destination,
            destination: filterCopy.origin
          };
          filters.push(filterConfig);
          fetchHistograms(filters).then((responses) =>
            handleHistogramResponses(responses, filters)
          );
        } else {
          axiosWrapper(
            {
              method: histogramApiSettings.method,
              url: `${histogramApiSettings.endPoint}`,
              headers: { ...histogramApiSettings.headers },
              data: transformHistogramSmart(filterCopy)
            },
            {
              resource: 'INTEGRATION__HISTOGRAM',
              meta,
              integrationSettings,
              pageContext,
              cache,
              cacheDuration
            }
          )
            .then((response) => {
              const validation = validateHistogramResponse(response.data);
              if (validation.isValid) {
                logger.info('INTEGRATION__HISTOGRAM: Successful request', response.data);
                const histograms = transformData(response.data, filterCopy);
                if (
                  filterCopy.dateRange?.action?.includes('combinable-destination-origin') &&
                  filterCopy.dateRange?.action?.includes('filter-change')
                ) {
                  const filterData = { ...filter, journeyType: 'ONE_WAY' };
                  handlerHistogramResponse(histograms, filterData).then(resolve).catch(reject);
                } else if (filterCopy.dateRange?.action?.includes('update-dparture-fare')) {
                  const filterData = { ...filter, journeyType: 'ONE_WAY' };
                  handleHistogramResponse(histograms, filterData).then(resolve).catch(reject);
                } else if (
                  !filterCopy.dateRange?.action?.includes('combinable') &&
                  filterCopy.dateRange?.action?.includes('filter-change') &&
                  filterCopy.dateRange?.departureDate &&
                  filterCopy.dateRange?.returnDate
                ) {
                  if (filter.dateRange?.action?.includes('interval-completed_filter-change')) {
                    handleHistogramResponseInterval(histograms, filterCopy)
                      .then(resolve)
                      .catch(reject);
                  } else {
                    handleHistogramResults(histograms, filterCopy).then(resolve).catch(reject);
                  }
                } else if (
                  !filterCopy.dateRange?.action?.includes('combinable') &&
                  filterCopy.dateRange?.action?.includes('filter-change') &&
                  filterCopy.dateRange?.departureDate &&
                  !filterCopy.dateRange?.returnDate &&
                  filterCopy.journeyType === 'ONE_WAY'
                ) {
                  handleHistogramResponse(histograms, filterCopy).then(resolve).catch(reject);
                } else {
                  resolve(histograms);
                }
              } else {
                logger.error(validation.message, validation.data);
                reject(response.data);
              }
            })
            .catch((err) => {
              reject(err);
            });
        }
      }
    }
  });
};

export { getHistogramsSmart, transformData, lowestFare, validateHistogramResponse };
